import { formatAmountWithUnit } from "@loot-os/api-contract";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { CommandItem } from "@loot-os/ui/components/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  CATALOG_PICKER_SORT_LABELS,
  CATALOG_PICKER_TAG_LABELS,
  CATALOG_PICKER_TYPES,
  type CatalogPickerSort,
  type CatalogPickerType,
} from "../lib/catalogPicker";
import { useCurrency } from "../lib/currency";
import type { PickedCatalogItem } from "../lib/dropTableDraft";
import { trpcClient } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";
import { PickerCombobox } from "./PickerCombobox";

/** Picks one catalog item for a drop-table row. */
export function CatalogItemPicker({
  value,
  placeholder,
  sharedOnly,
  taken,
  disabled,
  onPick,
}: {
  /** The row's current item, or null while it is still empty. */
  value: PickedCatalogItem | null;
  /** Trigger text while `value` is null; the default invites a first pick. */
  placeholder?: string;
  /** Box templates may only contain items every tenant can open. */
  sharedOnly: boolean;
  /** Items another row of the table already names: a drop table names each item once. */
  taken: Set<string>;
  disabled?: boolean;
  onPick: (item: PickedCatalogItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const currency = useCurrency();
  const debounced = useDebounced(query);

  const [type, setType] = useState<CatalogPickerType>("all");
  const [tag, setTag] = useState("all");
  const [sort, setSort] = useState<CatalogPickerSort>("value_desc");

  const results = useQuery({
    queryKey: [
      sharedOnly ? "platform.boxTemplates.searchCatalog" : "fulfillment.catalog.search",
      debounced,
      type,
      tag,
      sort,
    ],
    queryFn: () => {
      const input = {
        ...(debounced ? { query: debounced } : {}),
        ...(type !== "all" ? { availabilityStrategy: type } : {}),
        ...(tag !== "all" ? { tag } : {}),
        sort,
        limit: 20,
      };
      // The box template editor has no tenant, so it asks the platform router for shared rows.
      return sharedOnly
        ? trpcClient.platform.boxTemplates.searchCatalog.query(input)
        : trpcClient.fulfillment.catalog.search.query(input);
    },
    enabled: open,
  });

  return (
    <PickerCombobox
      open={open}
      onOpenChange={setOpen}
      query={query}
      onQueryChange={setQuery}
      triggerLabel={value?.name || placeholder || "Choose an item…"}
      searchPlaceholder="Search the catalog…"
      emptyMessage={
        results.isFetching ? "Searching…" : (results.error?.message ?? "No matching catalog items.")
      }
      disabled={disabled}
      filters={
        <>
          <FilterSelect
            label="Type"
            value={type}
            items={CATALOG_PICKER_TYPES}
            onChange={(value) => setType(value as CatalogPickerType)}
          />
          <FilterSelect
            label="Tag"
            value={tag}
            items={CATALOG_PICKER_TAG_LABELS}
            onChange={setTag}
          />
          <FilterSelect
            label="Sort"
            value={sort}
            items={CATALOG_PICKER_SORT_LABELS}
            onChange={(value) => setSort(value as CatalogPickerSort)}
          />
        </>
      }
    >
      {(results.data?.items ?? []).map((item) => (
        <CommandItem
          key={item.id}
          value={item.id}
          onSelect={() => {
            onPick({
              catalogItemId: item.id,
              name: item.name,
              imageUrl: item.overrideImage ?? item.image ?? "",
              value: item.value ?? 0,
              marketPriceCents: item.marketPrice ?? null,
            });
            setOpen(false);
          }}
          // An item with no value cannot be published, nor one another row names: the server
          // rejects both.
          disabled={item.value === null || taken.has(item.id)}
        >
          <BoxImage
            src={item.overrideImage ?? item.image ?? ""}
            className="size-8 shrink-0 rounded border object-contain"
          />
          <span className="truncate">{item.name}</span>
          <span className="ml-auto font-mono text-muted-foreground text-xs">
            {taken.has(item.id)
              ? "in the table"
              : item.value === null
                ? "no value"
                : formatAmountWithUnit(item.value, currency)}
          </span>
        </CommandItem>
      ))}
    </PickerCombobox>
  );
}

/** One of the picker's filter selects: a closed set of labelled values. */
function FilterSelect({
  label,
  value,
  items,
  onChange,
}: {
  label: string;
  value: string;
  items: Record<string, string>;
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} items={items} onValueChange={(next) => onChange(next as string)}>
      <SelectTrigger className="h-8 min-w-0 flex-1" size="sm" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(items).map(([key, text]) => (
          <SelectItem key={key} value={key}>
            {text}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
